import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Search, PencilLine, Check } from "lucide-react";
import { api } from "../lib/api";

const inputCls = "w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 outline-none focus:border-[#d4af37] transition-colors duration-200";

export const formatCp = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 7);
  return d.length > 4 ? `${d.slice(0, 4)}-${d.slice(4)}` : d;
};

/** Address fields from an apartment doc (legacy `address` goes into street). */
export const addressFromApt = (apt = {}) => ({
  postal_code: apt.postal_code || "",
  street: apt.street || (!apt.postal_code ? apt.address || "" : ""),
  door: apt.door || "",
  locality: apt.locality || "",
  concelho: apt.concelho || "",
  lat: null,
  lng: null,
});

export const EMPTY_ADDRESS = addressFromApt({});

const Field = ({ label, value, onChange, testId, placeholder }) => (
  <label className="block space-y-2">
    <span className="text-xs uppercase tracking-widest text-[#a0b2af]">{label}</span>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
           className={inputCls} data-testid={testId} />
  </label>
);

function StreetList({ result, onPick }) {
  return (
    <div className="border border-[#d4af37]/40 bg-[#041f1b] rounded-sm max-h-64 overflow-y-auto" data-testid="cp-results">
      <div className="px-4 py-2 text-[11px] uppercase tracking-widest text-[#d4af37] border-b border-[#1a4640] sticky top-0 bg-[#041f1b]">
        {result.localidade} · {result.concelho} · {result.distrito}
      </div>
      {result.streets.length === 0 && (
        <button type="button" onClick={() => onPick({ street: "", local: "" })}
                className="w-full text-left px-4 py-3 text-sm hover:bg-[#0a3a33]" data-testid="cp-result-locality-only">
          Usar apenas localidade e concelho
        </button>
      )}
      {result.streets.map((s, i) => (
        <button key={`${s.street}-${s.local}-${i}`} type="button" onClick={() => onPick(s)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#0a3a33] transition-colors duration-150 flex items-start gap-3 border-b border-[#1a4640]/50 last:border-0"
                data-testid={`cp-result-${i}`}>
          <MapPin size={14} className="text-[#d4af37] mt-0.5 shrink-0" />
          <span className="min-w-0">
            <span className="text-[#fdfbf7]">{s.street || s.local}</span>
            {((s.street && s.local) || s.detail) && (
              <span className="block text-[11px] text-[#7d918d]">{[s.street ? s.local : "", s.detail].filter(Boolean).join(" · ")}</span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Postal-code-first address block (Portugal, incl. Madeira). Typing a full
 * 0000-000 code lists matching streets; picking one fills street, locality and
 * concelho. Every field stays editable.
 */
export default function PostalAddressFields({ value, onChange }) {
  const [state, setState] = useState({ loading: false, result: null, error: "" });
  const [open, setOpen] = useState(false);
  const lastCp = useRef("");
  const initialCp = useRef(value.postal_code || "");
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const latest = useRef(null);
  latest.current = { value, onChange };

  useEffect(() => {
    const cp = value.postal_code || "";
    if (!/^\d{4}-\d{3}$/.test(cp) || cp === lastCp.current) return;
    lastCp.current = cp;
    let alive = true;
    setState({ loading: true, result: null, error: "" });
    const t = setTimeout(() => {
      api.get(`/postal-codes/${cp}`)
        .then(({ data }) => {
          if (!alive) return;
          setState({ loading: false, result: data, error: "" });
          const typed = cp !== initialCp.current;
          setOpen(typed);
          if (typed) {
            const { value: v, onChange: change } = latest.current;
            change({ ...v, locality: data.localidade || data.designacao, concelho: data.concelho });
          }
        })
        .catch((err) => alive && setState({
          loading: false, result: null,
          error: err?.response?.data?.detail || "Não foi possível pesquisar — introduza manualmente.",
        }));
    }, 250);
    return () => { alive = false; clearTimeout(t); lastCp.current = ""; };
  }, [value.postal_code]);

  function pick(s) {
    const r = state.result;
    onChange({
      ...value,
      street: s.street ? (s.local ? `${s.street}, ${s.local}` : s.street) : value.street,
      locality: (!s.street && s.local) || r.localidade || r.designacao,
      concelho: r.concelho,
      lat: r.lat ?? null, lng: r.lng ?? null,
    });
    setOpen(false);
  }

  return (
    <div className="md:col-span-2 space-y-4" data-testid="postal-address-fields">
      <div className="grid md:grid-cols-[220px_1fr] gap-4 items-start">
        <label className="block space-y-2">
          <span className="text-xs uppercase tracking-widest text-[#a0b2af]">Código postal</span>
          <div className="relative">
            <input inputMode="numeric" value={value.postal_code || ""} placeholder="9000-000"
                   onChange={(e) => {
                     initialCp.current = "";
                     lastCp.current = "";
                     setState((s) => ({ ...s, result: null, error: "" }));
                     onChange({ ...value, postal_code: formatCp(e.target.value), lat: null, lng: null });
                   }}
                   className={`${inputCls} pr-10 font-mono tracking-wider`} data-testid="cp-input" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]">
              {state.loading ? <Loader2 size={16} className="animate-spin" /> : state.result ? <Check size={16} /> : <Search size={16} className="text-[#7d918d]" />}
            </span>
          </div>
        </label>
        <div className="md:pt-7 text-xs text-[#a0b2af] min-h-[20px]" data-testid="cp-status">
          {state.error && <span className="text-amber-300">{state.error}</span>}
          {!state.error && state.result && !open && (
            <button type="button" onClick={() => setOpen(true)} className="text-[#d4af37] hover:underline inline-flex items-center gap-1"
                    data-testid="cp-show-results">
              <MapPin size={12} /> Ver {state.result.streets.length} rua(s) de {state.result.localidade}
            </button>
          )}
          {!state.error && !state.result && !state.loading && "Escreva o código postal para preencher a morada automaticamente."}
          {open && state.result && (
            <button type="button" onClick={() => setOpen(false)} className="text-[#a0b2af] hover:text-[#fdfbf7] inline-flex items-center gap-1"
                    data-testid="cp-manual-btn">
              <PencilLine size={12} /> Introduzir manualmente
            </button>
          )}
        </div>
      </div>
      {open && state.result && <StreetList result={state.result} onPick={pick} />}
      <div className="grid md:grid-cols-[1fr_200px] gap-4">
        <Field label="Rua" value={value.street} onChange={set("street")} testId="addr-street" placeholder="Rua, avenida, caminho…" />
        <Field label="Nº / Andar" value={value.door} onChange={set("door")} testId="addr-door" placeholder="12, 2º Esq." />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Localidade" value={value.locality} onChange={set("locality")} testId="addr-locality" />
        <Field label="Concelho" value={value.concelho} onChange={set("concelho")} testId="addr-concelho" />
      </div>
    </div>
  );
}
